/**
 * Safe Fallback Engine for Bilingual Support (Arabic / English)
 * 
 * Rules:
 * 1. If Arabic is selected:
 *    - If Arabic value is present and non-empty, return Arabic.
 *    - If Arabic value is empty/null but English is present, return English automatically.
 * 2. If English is selected:
 *    - If English value is present and non-empty, return English.
 *    - If English value is empty/null but Arabic is present, return Arabic automatically.
 * 3. Never return an empty string if any language has content.
 */

export function isArabicLang(lang?: string): boolean {
  if (!lang) return true;
  return lang.toLowerCase().startsWith('ar');
}

export function isRTL(lang?: string): boolean {
  return isArabicLang(lang);
}

/**
 * Returns localized string with bidirectional safe fallback.
 */
export function getLocalized(
  lang: string | undefined,
  arValue?: string | null,
  enValue?: string | null
): string {
  const isAr = isArabicLang(lang);
  const arTrimmed = (arValue !== undefined && arValue !== null) ? String(arValue).trim() : '';
  const enTrimmed = (enValue !== undefined && enValue !== null) ? String(enValue).trim() : '';

  if (isAr) {
    return arTrimmed !== '' ? arTrimmed : enTrimmed;
  } else {
    return enTrimmed !== '' ? enTrimmed : arTrimmed;
  }
}

/**
 * Returns localized array with bidirectional safe fallback.
 */
export function getLocalizedArray<T = any>(
  lang: string | undefined,
  arArray?: T[] | null,
  enArray?: T[] | null
): T[] {
  const isAr = isArabicLang(lang);
  const hasAr = Array.isArray(arArray) && arArray.length > 0;
  const hasEn = Array.isArray(enArray) && enArray.length > 0;

  if (isAr) {
    return hasAr ? arArray! : (hasEn ? enArray! : []);
  } else {
    return hasEn ? enArray! : (hasAr ? arArray! : []);
  }
}

/**
 * Helper to get a localized field from an object with standard key pairs:
 * e.g. item.name / item.nameEn, item.title / item.titleEn, item.label / item.labelEn, etc.
 */
export function getLocalizedProp(
  lang: string | undefined,
  item: any,
  baseKey: string,
  enKeyOverride?: string
): string {
  if (!item || typeof item !== 'object') return '';

  const enKey = enKeyOverride || `${baseKey}En`;
  const snakeEnKey = `${baseKey}_en`;

  const arVal = item[baseKey];
  const enVal = item[enKey] !== undefined ? item[enKey] : item[snakeEnKey];

  return getLocalized(lang, arVal, enVal);
}

/**
 * Returns an object localized if it has language subkeys or localized values
 */
export function getLocalizedObject<T = any>(
  lang: string | undefined,
  arObj?: T | null,
  enObj?: T | null
): T {
  const isAr = isArabicLang(lang);
  if (isAr) {
    return arObj || enObj || ({} as T);
  } else {
    return enObj || arObj || ({} as T);
  }
}
